import { useState, useEffect, useMemo } from "react";
import { message } from "antd";
import participanteVisitaService from "../../services/participantesVistas/participanteVisitaService";

export function useParticipantesData() {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await participanteVisitaService.getArchivosParticipantes();
        setRawData(data);
      } catch (error) {
        message.error("Error al cargar datos de participantes.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const data = useMemo(() => {
    if (!rawData || !rawData.data) return null;

    const participantes = rawData.data;

    const totalArchivos = participantes.reduce((total, p) => {
      return total + Object.values(p.resultados || {}).reduce((sum, proyecto) => sum + (proyecto.total || 0), 0);
    }, 0);

    const metasProyectos = new Set();
    participantes.forEach((p) => {
      metasProyectos.add(p.meta_x_proyecto || 0);
    });
    const metaGeneral = Array.from(metasProyectos).reduce((sum, meta) => sum + meta, 0);

    const periodosMap = new Map();
    participantes.forEach((p) => {
      Object.values(p.resultados || {}).forEach((proyecto) => {
        Object.entries(proyecto.subcarpetas || {}).forEach(([periodo, cantidad]) => {
          if (!periodosMap.has(periodo)) {
            periodosMap.set(periodo, { participantes: new Set(), archivos: 0 });
          }
          periodosMap.get(periodo).participantes.add(p.oid);
          periodosMap.get(periodo).archivos += cantidad;
        });
      });
    });

    const periodos = Array.from(periodosMap.entries())
      .map(([periodo, info]) => ({
        periodo,
        participantes: info.participantes.size,
        archivos: info.archivos,
        fecha: parsePeriodoToDate(periodo),
      }))
      .sort((a, b) => a.fecha.getTime() - b.fecha.getTime());

    const proyectosMap = new Map();
    participantes.forEach((p) => {
      if (!proyectosMap.has(p.proyectos)) {
        proyectosMap.set(p.proyectos, {
          nombre: p.proyectos,
          totalParticipantes: 0,
          participantesConArchivos: 0,
          participantesSinArchivos: 0,
          metaProyecto: p.meta_x_proyecto || 0,
          archivosEjecutados: 0,
        });
      }
      const stats = proyectosMap.get(p.proyectos);
      stats.totalParticipantes++;
      if (p.tiene_archivos) stats.participantesConArchivos++;
      else stats.participantesSinArchivos++;

      Object.values(p.resultados || {}).forEach((proyecto) => {
        stats.archivosEjecutados += proyecto.total || 0;
      });
    });

    const proyectos = Array.from(proyectosMap.values());
    const departamentos = Array.from(new Set(participantes.map((p) => p.departamento)));
    const proyectosUnicos = Array.from(new Set(participantes.map((p) => p.proyectos)));
    const municipios = Array.from(new Set(participantes.map((p) => p.municipio)));

    const porcentajeAvanceGeneral = metaGeneral > 0 ? Math.round((totalArchivos / metaGeneral) * 100) : 0;

    return {
      totalParticipantes: rawData.total_participantes,
      participantesConArchivos: rawData.participantes_con_archivos,
      participantesSinArchivos: rawData.total_participantes - rawData.participantes_con_archivos,
      totalArchivos,
      metaGeneral,
      porcentajeAvanceGeneral,
      participantes,
      periodos,
      proyectos,
      visitasRealizadas: 85,
      metaVisitas: 100,
      visitasProyectadas: 120,
      porcentajeAvance: 85,
      departamentos,
      proyectosUnicos,
      municipios,
    };
  }, [rawData]);

  return { data, loading };
}

function parsePeriodoToDate(periodo) {
  const match = periodo.match(/(\w+)-?(\w+)?\s+(\d{4})/);
  if (match) {
    const [, mes1, , año] = match;
    const meses = {
      enero: 0,
      febrero: 1,
      marzo: 2,
      abril: 3,
      mayo: 4,
      junio: 5,
      julio: 6,
      agosto: 7,
      septiembre: 8,
      octubre: 9,
      noviembre: 10,
      diciembre: 11,
    };
    return new Date(parseInt(año), meses[mes1.toLowerCase()] || 0, 1);
  }
  return new Date();
}
